import React, { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { 
    Trash2, 
    Info, 
    Users, 
    MessageSquare, 
    Search, 
    Calendar, 
    TrendingUp, 
    AlertCircle,
    ChevronDown,
    Eye
} from "lucide-react";
import { useToast } from "../context/ToastContext";

const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={dropdownRef} className="relative select-none">
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-2 border border-zinc-200 rounded-xl text-xs bg-white ${disabled
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:bg-zinc-50"
          } flex justify-between items-center transition-all font-semibold`}
      >
        <span className={selectedOption ? "text-zinc-900" : "text-zinc-400"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`transition-transform text-zinc-500 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 bg-white border border-zinc-200 rounded-xl mt-1 max-h-60 overflow-y-auto z-50 shadow-lg animate-in fade-in slide-in-from-top-1 duration-200">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`px-4 py-2.5 text-xs cursor-pointer hover:bg-zinc-50 font-semibold ${value === option.value
                ? "bg-zinc-100 text-zinc-900"
                : "text-zinc-700"
                }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const STATUS_OPTIONS = [
    { value: "all", label: "All Status" },
    { value: "sent", label: "Sent" },
    { value: "delivered", label: "Delivered" },
    { value: "read", label: "Read" },
    { value: "failed", label: "Failed" }
];

const Reports = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [, setSegments] = useState([]);
    const [waTemplates, setWaTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showToast } = useToast();

    // Deletion states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Detail states
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [drawerContacts, setDrawerContacts] = useState([]);
    const [campaignMessages, setCampaignMessages] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);

    // Search and filters
    const [campaignSearch, setCampaignSearch] = useState("");
    const [recipientSearch, setRecipientSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const fetchReports = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const segRes = await api.getSegments();
            const segmentsList = segRes?.segments || [];
            setSegments(segmentsList);

            try {
                const waTemplatesRes = await api.getWhatsAppTemplates();
                if (waTemplatesRes && waTemplatesRes.templates) {
                    setWaTemplates(waTemplatesRes.templates);
                }
            } catch (err) {
                console.error("Failed to fetch templates for reports preview:", err);
            }

            const waRes = await api.getWhatsAppCampaigns();
            if (waRes && waRes.campaigns) {
                const mapped = waRes.campaigns.map(w => {
                    const matchedSeg = segmentsList.find(s => String(s.id) === String(w.segment_id));
                    return {
                        id: w.id,
                        name: w.name,
                        segment_id: w.segment_id,
                        segment_name: matchedSeg?.name || w.segment_name || "Unknown Segment",
                        segment_contact_ids: matchedSeg?.contact_ids || [],
                        template_name: w.template_name,
                        language_code: w.language_code,
                        variables: w.variables,
                        scheduled_time: w.scheduled_time,
                        status: w.status,
                        contacts_count: w.contacts_count,
                        sent_count: w.sent_count,
                        failed_count: w.failed_count,
                        created_at: w.created_at,
                        isWhatsApp: true,
                        header_media_url: w.header_media_url,
                        previewTitle: `Template: ${w.template_name} | Segment: ${matchedSeg?.name || w.segment_name || "Segment"}`,
                        contact_ids: w.contact_ids || []
                    };
                });
                setCampaigns(mapped);

                // Use functional updater to avoid selectedCampaign as a dependency
                setSelectedCampaign(prev => {
                    if (!prev && mapped.length > 0) {
                        // Auto-select first campaign if none selected
                        return mapped[0];
                    } else if (prev) {
                        // Update active selection with fresh counts
                        const updated = mapped.find(c => c.id === prev.id);
                        return updated || prev;
                    }
                    return prev;
                });
            } else {
                setCampaigns([]);
            }
            setError(null);
        } catch (err) {
            console.error("Failed to load WhatsApp campaign reports:", err);
            setError("Failed to fetch campaign reports. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []); // ✅ Empty deps — no selectedCampaign dependency to break the loop

    // Initial Fetch
    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    // Polling for active processing campaigns
    useEffect(() => {
        const hasProcessing = campaigns.some(c => c.status === "PROCESSING");
        if (hasProcessing) {
            const interval = setInterval(() => {
                fetchReports(true);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [campaigns, fetchReports]);

    // Fetch segment contacts and message statuses when a campaign is selected
    useEffect(() => {
        const loadContacts = async () => {
            if (!selectedCampaign) {
                setDrawerContacts([]);
                setCampaignMessages([]);
                return;
            }
            setLoadingContacts(true);
            try {
                // Fetch campaign message details
                let msgList = [];
                try {
                    const msgRes = await api.getWhatsAppCampaignMessages(selectedCampaign.id);
                    if (msgRes && msgRes.success) {
                        msgList = msgRes.messages || [];
                    }
                } catch (err) {
                    console.error("Failed to load message statuses for campaign:", err);
                }
                setCampaignMessages(msgList);

                const res1 = await api.getContacts(1, 100);
                const res2 = await api.getContacts(2, 100);
                const allContacts = [...(res1?.contacts || []), ...(res2?.contacts || [])];
                
                const segmentContactIds = (selectedCampaign.contact_ids && selectedCampaign.contact_ids.length > 0)
                    ? selectedCampaign.contact_ids.map(id => String(id))
                    : selectedCampaign.segment_contact_ids.map(id => String(id));
                const filtered = allContacts.filter(c => segmentContactIds.includes(String(c.id)));
                setDrawerContacts(filtered);
            } catch (err) {
                console.error("Failed to load contacts for campaign details:", err);
                setDrawerContacts([]);
            } finally {
                setLoadingContacts(false);
            }
        };
        loadContacts();
    }, [selectedCampaign]);

    // WebSocket listener for real-time campaign status updates (no manual refresh)
    useEffect(() => {
        let socket = null;
        let reconnectTimeout = null;

        const connectWebSocket = () => {
            const activeAgentId = localStorage.getItem("activeAiAgentId");
            const storedUser = sessionStorage.getItem('user');
            let token = null;
            if (storedUser) {
                try {
                    token = JSON.parse(storedUser).token;
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }
            if (!activeAgentId || !token) return;

            const apiBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
            const isHttps = apiBase.startsWith('https://') || window.location.protocol === 'https:';
            const protocol = isHttps ? 'wss:' : 'ws:';
            const wsHost = apiBase.replace(/^https?:\/\//, '').replace(/\/api$/, '');
            const wsUrl = `${protocol}//${wsHost}/api/ws/ai-agents?ai_agent_id=${activeAgentId}&token=${token}`;

            socket = new WebSocket(wsUrl);

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === "CAMPAIGN_STATUS_UPDATE") {
                        const { campaign_id, message_id, status, error_message } = data;
                        
                        // Silent refresh of master campaigns list
                        fetchReports(true);

                        // If the updated campaign is currently selected, update message statuses in real-time
                        setSelectedCampaign(prevSelected => {
                            if (prevSelected && prevSelected.id === campaign_id) {
                                setCampaignMessages(prevMsgs => {
                                    const exists = prevMsgs.some(m => m.message_id === message_id);
                                    if (exists) {
                                        return prevMsgs.map(m => {
                                            if (m.message_id === message_id) {
                                                return { ...m, status, error_message, updated_at: new Date().toISOString() };
                                            }
                                            return m;
                                        });
                                    } else {
                                        return [...prevMsgs, { message_id, status, error_message, updated_at: new Date().toISOString() }];
                                    }
                                });
                            }
                            return prevSelected;
                        });
                    }
                } catch (err) {
                    console.error("Error handling reports websocket event:", err);
                }
            };

            socket.onclose = () => {
                reconnectTimeout = setTimeout(connectWebSocket, 3000);
            };

            socket.onerror = () => {
                if (socket) socket.close();
            };
        };

        connectWebSocket();

        return () => {
            if (socket) socket.close();
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
        };
    }, [fetchReports]);

    const handleDeleteRequest = (e, campaign) => {
        e.stopPropagation();
        setCampaignToDelete(campaign.id);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!campaignToDelete) return;
        setIsDeleting(true);
        try {
            await api.deleteWhatsAppCampaign(campaignToDelete);
            showToast("Report deleted successfully", "success");
            
            // If the deleted campaign was selected, reset selection
            if (selectedCampaign && selectedCampaign.id === campaignToDelete) {
                setSelectedCampaign(null);
            }
            
            setCampaignToDelete(null);
            setIsDeleteModalOpen(false);
            await fetchReports();
        } catch (err) {
            showToast("Failed to delete report: " + (err.message || "Unknown error"), "error");
        } finally {
            setIsDeleting(false);
        }
    };

    // Filter campaigns in sidebar
    const filteredCampaigns = campaigns.filter(c => 
        c.name.toLowerCase().includes(campaignSearch.toLowerCase()) ||
        c.template_name.toLowerCase().includes(campaignSearch.toLowerCase())
    );

    // Filter recipients in workspace
    const filteredRecipients = drawerContacts.filter(contact => {
        const matchesSearch = 
            (contact.name || "").toLowerCase().includes(recipientSearch.toLowerCase()) ||
            (contact.phone || "").toLowerCase().includes(recipientSearch.toLowerCase());
        
        if (!matchesSearch) return false;
        if (statusFilter === "all") return true;

        const matchedMsg = campaignMessages.find(m => 
            String(m.contact_id) === String(contact.id) || 
            String(m.phone).replace(/\D/g, '') === String(contact.phone).replace(/\D/g, '')
        );
        const msgStatus = matchedMsg ? matchedMsg.status : (selectedCampaign.status === "COMPLETED" ? "sent" : "queued");
        return msgStatus === statusFilter;
    });

    return (
        <div className="flex flex-col h-screen bg-transparent w-full">
            {/* Custom high-end animations */}
            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(12px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes slideInFromRight {
                    from {
                        opacity: 0;
                        transform: translateX(16px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
                @keyframes pulseAccent {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.15);
                    }
                    50% {
                        box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.05);
                    }
                }
                .animate-fade-up {
                    animation: fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                .animate-slide-right {
                    animation: slideInFromRight 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                .pulse-active {
                    animation: pulseAccent 2s infinite ease-in-out;
                }
                .delay-80 { animation-delay: 80ms; }
                .delay-160 { animation-delay: 160ms; }
                .delay-240 { animation-delay: 240ms; }
                .delay-320 { animation-delay: 320ms; }
            `}</style>

            {loading ? (
                <div className="flex-1 flex overflow-hidden animate-pulse">
                    {/* ── LEFT PANEL SKELETON (70%) ── */}
                    <div className="flex-1 flex flex-col overflow-y-auto p-8 border-r border-zinc-100 min-w-0 space-y-6">
                        {/* Title & Action Row Skeleton */}
                        <div className="flex items-start justify-between gap-4">
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <div className="h-6 w-48 bg-zinc-200 rounded-lg" />
                                    <div className="h-5 w-16 bg-zinc-100 rounded-lg" />
                                </div>
                                <div className="h-3.5 w-64 bg-zinc-100 rounded" />
                            </div>
                            <div className="w-9 h-9 rounded-xl bg-zinc-100 shrink-0" />
                        </div>

                        {/* 5 Metrics Cards Skeleton */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="rounded-xl p-4 border border-zinc-100 flex items-center gap-3 bg-zinc-50/40">
                                    <div className="w-8 h-8 rounded-lg bg-zinc-200/70 shrink-0" />
                                    <div className="space-y-1.5 flex-1">
                                        <div className="h-4 w-12 bg-zinc-200 rounded" />
                                        <div className="h-2.5 w-16 bg-zinc-100 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Config Setup Card Skeleton */}
                        <div className="rounded-xl p-6 border border-zinc-100 space-y-4 bg-zinc-50/30">
                            <div className="h-3.5 w-36 bg-zinc-200 rounded" />
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="p-3 rounded-lg border border-zinc-100 bg-white space-y-2">
                                        <div className="h-2.5 w-20 bg-zinc-100 rounded" />
                                        <div className="h-3.5 w-28 bg-zinc-200 rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recipient List Skeleton */}
                        <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="h-3.5 w-40 bg-zinc-200 rounded" />
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-44 bg-zinc-100 rounded-xl" />
                                    <div className="h-8 w-28 bg-zinc-100 rounded-xl" />
                                </div>
                            </div>
                            <div className="rounded-xl border border-zinc-100 overflow-hidden divide-y divide-zinc-50">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="p-4 flex items-center justify-between bg-zinc-50/20">
                                        <div className="space-y-1.5">
                                            <div className="h-3.5 w-32 bg-zinc-200 rounded" />
                                            <div className="h-2.5 w-24 bg-zinc-100 rounded" />
                                        </div>
                                        <div className="h-5 w-16 bg-zinc-100 rounded-md" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ── RIGHT PANEL SKELETON (30% / 340px) ── */}
                    <div className="w-[340px] flex-shrink-0 bg-transparent flex flex-col border-l border-zinc-100">
                        {/* Sidebar Header & Search Skeleton */}
                        <div className="p-6 border-b border-zinc-100 space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="h-3.5 w-24 bg-zinc-200 rounded" />
                                <div className="h-4 w-6 bg-zinc-100 rounded-lg" />
                            </div>
                            <div className="h-9 w-full bg-zinc-100 rounded-xl" />
                        </div>

                        {/* Sidebar Campaign Cards Skeleton */}
                        <div className="flex-1 divide-y divide-zinc-50 p-2 space-y-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="p-4 rounded-xl border border-zinc-100/60 bg-zinc-50/30 space-y-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="h-3.5 w-36 bg-zinc-200 rounded" />
                                        <div className="h-4 w-12 bg-zinc-100 rounded-md" />
                                    </div>
                                    <div className="h-2.5 w-24 bg-zinc-100 rounded" />
                                    <div className="flex gap-2 pt-1">
                                        <div className="h-3 w-16 bg-zinc-100 rounded" />
                                        <div className="h-3 w-16 bg-zinc-100 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : error ? (
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center p-6 bg-red-50 text-red-500 rounded-xl border border-red-100 text-sm font-medium">{error}</div>
                </div>
            ) : campaigns.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center bg-white m-6 sm:m-8 rounded-2xl border border-zinc-200/80 shadow-xs p-8 text-center">
                    <h3 className="text-sm font-semibold text-zinc-900 tracking-tight">No Reports Found</h3>
                    <p className="text-zinc-500 text-xs mt-1 max-w-sm leading-relaxed">
                        Send templates using the WhatsApp campaign tool to see dispatch statistics.
                    </p>
                </div>
            ) : (
                <div className="flex-1 flex overflow-hidden">
                    {/* ── LEFT PANEL: Campaign WorkSpace (70%) ── */}
                    <div className="flex-1 flex flex-col overflow-y-auto p-8 border-r border-zinc-100 min-w-0">
                        {selectedCampaign ? (
                            <div key={selectedCampaign.id} className="space-y-6">
                                {/* Title & Action Row */}
                                <div className="flex items-start justify-between gap-4 animate-fade-up">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-base font-black text-zinc-900 uppercase tracking-tight">
                                                {selectedCampaign.name}
                                            </h2>
                                            <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-semibold text-zinc-500 text-white ${
                                                selectedCampaign.status === 'COMPLETED' ? 'bg-emerald-500' :
                                                selectedCampaign.status === 'PENDING' ? 'bg-indigo-600' :
                                                selectedCampaign.status === 'FAILED' ? 'bg-rose-500' : 'bg-amber-500'
                                            }`}>
                                                {selectedCampaign.status === 'COMPLETED' ? 'Sent' :
                                                 selectedCampaign.status === 'PENDING' ? 'Scheduled' :
                                                 selectedCampaign.status === 'PROCESSING' ? 'Sending' : 'Failed'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] font-medium text-black mt-1">
                                            {selectedCampaign.previewTitle}
                                        </p>
                                    </div>

                                    <button 
                                        onClick={(e) => handleDeleteRequest(e, selectedCampaign)}
                                        className="w-9 h-9 flex items-center justify-center rounded-xl border border-black-100 text-red-500 hover:text-red-500 hover:bg-red-50 hover:border-red-100 transition-all shrink-0 bg-white shadow-2xs"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>

                                {/* Full-Width Metrics Row */}
                                {(() => {
                                    const hasMsgs = campaignMessages && campaignMessages.length > 0;
                                    const sentVal = hasMsgs ? campaignMessages.filter(m => m.status === 'sent').length : selectedCampaign.sent_count;
                                    const deliveredVal = hasMsgs ? campaignMessages.filter(m => m.status === 'delivered').length : (selectedCampaign.delivered_count || 0);
                                    const readVal = hasMsgs ? campaignMessages.filter(m => m.status === 'read').length : (selectedCampaign.read_count || 0);
                                    const failedVal = hasMsgs ? campaignMessages.filter(m => m.status === 'failed').length : selectedCampaign.failed_count;

                                    return (
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 animate-fade-up delay-80">
                                            <div className="bg-transparent rounded-xl p-4 border border-zinc-100 flex items-center gap-3 transition-all">
                                                <Users className="text-zinc-400 shrink-0" size={18} />
                                                <div>
                                                    <div className="text-base font-black text-zinc-900">{selectedCampaign.contacts_count}</div>
                                                    <div className="text-[8px] font-bold text-zinc-400 uppercase tracking-wider mt-0.5">Total Targets</div>
                                                </div>
                                            </div>
                                            <div className="bg-transparent rounded-xl p-4 border border-zinc-100 flex items-center gap-3 transition-all">
                                                <MessageSquare className="text-zinc-400 shrink-0" size={18} />
                                                <div>
                                                    <div className="text-base font-black text-zinc-900">{sentVal}</div>
                                                    <div className="text-[8px] font-bold text-zinc-400 uppercase tracking-wider mt-0.5">Sent</div>
                                                </div>
                                            </div>
                                            <div className="bg-transparent rounded-xl p-4 border border-zinc-100 flex items-center gap-3 transition-all">
                                                <TrendingUp className="text-zinc-400 shrink-0" size={18} />
                                                <div>
                                                    <div className="text-base font-black text-zinc-900">{deliveredVal}</div>
                                                    <div className="text-[8px] font-bold text-zinc-400 uppercase tracking-wider mt-0.5">Delivered</div>
                                                </div>
                                            </div>
                                            <div className="bg-transparent rounded-xl p-4 border border-zinc-100 flex items-center gap-3 transition-all">
                                                <Eye className="text-zinc-400 shrink-0" size={18} />
                                                <div>
                                                    <div className="text-base font-black text-zinc-900">{readVal}</div>
                                                    <div className="text-[8px] font-bold text-zinc-400 uppercase tracking-wider mt-0.5">Read</div>
                                                </div>
                                            </div>
                                            <div className="bg-transparent rounded-xl p-4 border border-zinc-100 flex items-center gap-3 transition-all">
                                                <AlertCircle className="text-zinc-400 shrink-0" size={18} />
                                                <div>
                                                    <div className="text-base font-black text-zinc-900">{failedVal}</div>
                                                    <div className="text-[8px] font-bold text-zinc-400 uppercase tracking-wider mt-0.5">Failed</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Config Details */}
                                <div className="bg-transparent rounded-xl p-6 border border-zinc-100 space-y-4 animate-fade-up delay-160 transition-all">
                                    <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider font-semibold flex items-center gap-2">
                                        <Info size={13} />
                                        <span>Configuration Setup</span>
                                    </h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-xs">
                                        <div>
                                            <label className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Template Name</label>
                                            <span className="font-semibold text-zinc-800 block mt-1">{selectedCampaign.template_name}</span>
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Language Code</label>
                                            <span className="font-semibold text-zinc-800 block mt-1 uppercase">{selectedCampaign.language_code}</span>
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Contact Segment</label>
                                            <span className="font-semibold text-zinc-800 block mt-1">{selectedCampaign.segment_name}</span>
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Created At</label>
                                            <span className="font-semibold text-zinc-800 block mt-1">{new Date(selectedCampaign.created_at).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Template Details & Live Preview */}
                                {(() => {
                                    const activeTemplate = waTemplates?.find(t => t.name === selectedCampaign.template_name);
                                    if (!activeTemplate) return null;

                                    const headerComp = activeTemplate.components?.find(c => c.type === "HEADER");
                                    const bodyComp = activeTemplate.components?.find(c => c.type === "BODY");
                                    const footerComp = activeTemplate.components?.find(c => c.type === "FOOTER");
                                    const buttonsComp = activeTemplate.components?.find(c => c.type === "BUTTONS");

                                    // Resolve media preview URL if format is media
                                    let mediaPreviewUrl = null;
                                    if (headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                                        const url = selectedCampaign.header_media_url;
                                        if (url) {
                                            if (url.startsWith("media_id:")) {
                                                const mediaId = url.split(":")[2];
                                                const activeAiAgentId = localStorage.getItem("activeAiAgentId");
                                                const token = sessionStorage.getItem("user") ? JSON.parse(sessionStorage.getItem("user")).token : "";
                                                mediaPreviewUrl = `${process.env.REACT_APP_API_BASE_URL}/whatsapp/media/${mediaId}?ai_agent_id=${activeAiAgentId}&token=${token}`;
                                            } else {
                                                mediaPreviewUrl = url;
                                            }
                                        }
                                    }

                                    const substituteVars = (text) => {
                                        if (!text) return "";
                                        return text.replace(/\{\{(\d+)\}\}/g, (_, idx) => {
                                            const val = selectedCampaign.variables?.[idx - 1];
                                            return val ? val : `{{${idx}}}`;
                                        });
                                    };

                                    return (
                                        <div className="bg-transparent rounded-xl p-6 border border-zinc-100 space-y-4 animate-fade-up delay-240 transition-all">
                                            <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider font-semibold flex items-center gap-2">
                                                <Eye size={13} />
                                                <span>Template Content & Preview</span>
                                            </h3>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Left: Raw Components info */}
                                                <div className="space-y-4 text-xs text-zinc-700">
                                                    {headerComp && (
                                                        <div>
                                                            <span className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Header ({headerComp.format})</span>
                                                            <div className="mt-1 font-semibold">
                                                                {headerComp.format === "TEXT" ? headerComp.text : "[Media Asset Included]"}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {bodyComp && (
                                                        <div>
                                                            <span className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Body Text</span>
                                                            <div className="mt-1 font-semibold whitespace-pre-wrap">{bodyComp.text}</div>
                                                        </div>
                                                    )}
                                                    {footerComp && (
                                                        <div>
                                                            <span className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Footer Text</span>
                                                            <div className="mt-1 font-semibold">{footerComp.text}</div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Right: Simulated WhatsApp Chat Bubble Mockup */}
                                                <div className="flex justify-center bg-zinc-50/50 rounded-xl p-4 border border-zinc-100">
                                                    <div style={{
                                                        background: "#ffffff",
                                                        borderRadius: "12px",
                                                        padding: "12px",
                                                        width: "100%",
                                                        maxWidth: "280px",
                                                        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                                                        fontSize: "13px",
                                                        color: "#303030",
                                                        lineHeight: "1.4"
                                                    }}>
                                                        {headerComp && (
                                                            <div className="mb-2">
                                                                {headerComp.format === "TEXT" && (
                                                                    <div className="font-extrabold text-zinc-900">{substituteVars(headerComp.text)}</div>
                                                                )}
                                                                {headerComp.format === "IMAGE" && (
                                                                    <div className="w-full h-28 bg-zinc-100 rounded-lg overflow-hidden flex items-center justify-center relative">
                                                                        {mediaPreviewUrl ? (
                                                                            <>
                                                                                <img 
                                                                                    src={mediaPreviewUrl} 
                                                                                    alt="Template" 
                                                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                                                    onError={(e) => {
                                                                                        e.target.style.display = 'none';
                                                                                        e.target.nextSibling.style.display = 'inline-block';
                                                                                    }}
                                                                                />
                                                                                <span role="img" aria-label="image placeholder" style={{ fontSize: "24px", display: "none" }}>🖼️</span>
                                                                            </>
                                                                        ) : (
                                                                            <span role="img" aria-label="image placeholder" style={{ fontSize: "24px" }}>🖼️</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {headerComp.format === "VIDEO" && (
                                                                    <div className="w-full h-28 bg-zinc-100 rounded-lg overflow-hidden flex items-center justify-center relative">
                                                                        {mediaPreviewUrl ? (
                                                                            <>
                                                                                <video 
                                                                                    src={mediaPreviewUrl} 
                                                                                    controls 
                                                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                                                    onError={(e) => {
                                                                                        e.target.style.display = 'none';
                                                                                        e.target.nextSibling.style.display = 'inline-block';
                                                                                    }}
                                                                                />
                                                                                <span style={{ fontSize: "24px", display: "none" }}>📹</span>
                                                                            </>
                                                                        ) : (
                                                                            <span style={{ fontSize: "24px" }}>📹</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {headerComp.format === "DOCUMENT" && (
                                                                    <div className="w-full p-2 bg-zinc-50 border border-zinc-200 rounded-lg flex items-center gap-2">
                                                                        <span style={{ fontSize: "16px" }}>📄</span>
                                                                        <span className="text-[10px] font-bold text-zinc-600 truncate">Document Attached</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {bodyComp && (
                                                            <div style={{ whiteSpace: "pre-wrap" }}>{substituteVars(bodyComp.text)}</div>
                                                        )}

                                                        {footerComp && (
                                                            <div className="text-[10px] text-zinc-400 mt-1">{footerComp.text}</div>
                                                        )}

                                                        {buttonsComp && buttonsComp.buttons && (
                                                            <div className="border-t border-zinc-100 mt-3 pt-2 space-y-1">
                                                                {buttonsComp.buttons.map((btn, bidx) => (
                                                                    <div key={bidx} className="text-center text-xs font-bold text-blue-600 hover:text-blue-700 cursor-pointer">
                                                                        {btn.text}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Variable Mapping */}
                                {selectedCampaign.variables && selectedCampaign.variables.length > 0 && (
                                    <div className="bg-transparent rounded-xl p-6 border border-zinc-100 space-y-3 animate-fade-up delay-240 transition-all">
                                        <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider font-semibold flex items-center gap-2">
                                            <MessageSquare size={13} />
                                            <span>Dynamic Variable Mappings</span>
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                            {selectedCampaign.variables.map((val, idx) => (
                                                <div key={idx} className="flex items-center gap-3 bg-zinc-50 rounded-xl border border-zinc-100 p-3 text-xs hover:bg-zinc-100/50 transition-colors">
                                                    <span className="px-2 py-0.5 bg-blue-50 border border-blue-100 text-blue-600 rounded font-black text-[10px]">{`{{${idx + 1}}}`}</span>
                                                    <span className="text-zinc-700 font-bold truncate flex-1">{val}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Recipient Blast list */}
                                <div className="space-y-4 animate-fade-up delay-320">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                        <h3 className="text-[10px] font-bold text-black-400 uppercase tracking-wider font-semibold flex items-center gap-2">
                                            <Users size={13} />
                                            <span>Recipient list & status</span>
                                        </h3>
                                        
                                        {/* Filters inside workspace */}
                                        <div className="flex items-center gap-2">
                                            <div className="relative">
                                                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                                                <input
                                                    type="text"
                                                    value={recipientSearch}
                                                    onChange={(e) => setRecipientSearch(e.target.value)}
                                                    placeholder="Search contact..."
                                                    className="pl-9 pr-4 py-1.5 text-xs bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-0 focus:border-zinc-400 font-semibold"
                                                />
                                            </div>
                                            <div className="w-[120px]">
                                                <CustomDropdown
                                                    value={statusFilter}
                                                    onChange={setStatusFilter}
                                                    options={STATUS_OPTIONS}
                                                    placeholder="All Status"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {loadingContacts ? (
                                        <div className="space-y-2">
                                            {[1, 2, 3].map(i => (
                                                <div key={i} className="h-14 bg-transparent rounded-xl border border-zinc-100 animate-pulse" />
                                            ))}
                                        </div>
                                    ) : filteredRecipients.length === 0 ? (
                                        <div className="p-12 bg-transparent border border-zinc-100 rounded-xl text-center text-xs text-zinc-400 font-semibold">
                                            No recipients match your filters.
                                        </div>
                                    ) : (
                                        <div className="bg-transparent border border-zinc-100 rounded-xl overflow-hidden transition-all">
                                            <div className="divide-y divide-zinc-50 max-h-[400px] overflow-y-auto custom-scrollbar">
                                                {filteredRecipients.map(c => {
                                                    const matchedMsg = campaignMessages.find(m => 
                                                        String(m.contact_id) === String(c.id) || 
                                                        String(m.phone).replace(/\D/g, '') === String(c.phone).replace(/\D/g, '')
                                                    );
                                                    const msgStatus = matchedMsg ? matchedMsg.status : (selectedCampaign.status === "COMPLETED" ? "sent" : "queued");

                                                    return (
                                                        <div key={c.id} className="p-4 flex items-center justify-between hover:bg-zinc-50/50 transition-all">
                                                            <div>
                                                                <h4 className="text-xs font-black text-zinc-900">{c.name || 'WhatsApp Contact'}</h4>
                                                                <p className="text-[10px] font-bold text-zinc-400 mt-0.5">{c.phone}</p>
                                                            </div>
                                                            <div>
                                                                {msgStatus === 'read' ? (
                                                                    <span className="flex items-center gap-1 text-[9px] font-black uppercase text-blue-600">
                                                                        ✓✓ Read
                                                                    </span>
                                                                ) : msgStatus === 'delivered' ? (
                                                                    <span className="flex items-center gap-1 text-[9px] font-black uppercase text-zinc-900">
                                                                        ✓ Delivered
                                                                    </span>
                                                                ) : msgStatus === 'sent' ? (
                                                                    <span className="flex items-center gap-1 text-[9px] font-black uppercase text-zinc-600">
                                                                        ✓ Sent
                                                                    </span>
                                                                ) : msgStatus === 'failed' ? (
                                                                    <div title={matchedMsg?.error_message || "Delivery failed"} className="flex flex-col items-end gap-1">
                                                                        <span className="flex items-center gap-1 text-[9px] font-black uppercase text-rose-500 cursor-help">
                                                                            ✕ Failed
                                                                        </span>
                                                                        {matchedMsg?.error_message && (
                                                                            <span className="text-[8px] text-rose-400 font-medium max-w-[150px] truncate">
                                                                                {matchedMsg.error_message}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ) : selectedCampaign.status === 'PROCESSING' ? (
                                                                    <span className="flex items-center gap-1 text-[9px] font-black uppercase text-amber-600 animate-pulse">
                                                                        ⚡ Blasting...
                                                                    </span>
                                                                ) : (
                                                                    <span className="flex items-center gap-1 text-[9px] font-black uppercase text-zinc-900">
                                                                        🕒 Queued
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-center animate-fade-up">
                                <Info size={28} className="text-zinc-300 mb-2 animate-bounce" />
                                <p className="text-xs text-zinc-400 font-bold uppercase tracking-wider font-semibold">Select a campaign report to inspect</p>
                            </div>
                        )}
                    </div>

                    {/* ── RIGHT PANEL: Sidebar Master List (30% / 340px) ── */}
                    <div className="w-[340px] flex-shrink-0 bg-transparent flex flex-col overflow-y-auto">
                        {/* Sidebar Header & Search */}
                        <div className="p-6 border-b border-zinc-100 space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-zinc-400 uppercase tracking-wider font-semibold">All Dispatches</span>
                                <span className="text-[9px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-lg font-black">{filteredCampaigns.length}</span>
                            </div>
                            <div className="relative">
                                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                                <input
                                    type="text"
                                    value={campaignSearch}
                                    onChange={(e) => setCampaignSearch(e.target.value)}
                                    placeholder="Filter campaigns..."
                                    className="w-full pl-9 pr-4 py-2 text-xs bg-zinc-50 border border-zinc-100 rounded-xl focus:outline-none focus:ring-0 focus:border-zinc-400 focus:bg-white transition-all font-semibold text-zinc-900"
                                />
                            </div>
                        </div>

                        {/* List Items */}
                        <div className="flex-1 divide-y divide-zinc-50 overflow-y-auto custom-scrollbar">
                            {filteredCampaigns.map((campaign, index) => {
                                const isActive = selectedCampaign && selectedCampaign.id === campaign.id;
                                return (
                                    <div
                                        key={campaign.id}
                                        onClick={() => setSelectedCampaign(campaign)}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        className={`p-5 cursor-pointer flex flex-col gap-2.5 transition-all duration-300 animate-slide-right ${
                                            isActive ? "bg-zinc-100/90" : "hover:bg-zinc-50/50"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <h4 className={`text-xs font-bold uppercase tracking-tight truncate flex-1 transition-colors ${isActive ? "text-zinc-900" : "text-zinc-800"}`}>
                                                {campaign.name}
                                            </h4>
                                            <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider text-white transition-all ${
                                                campaign.status === 'COMPLETED' ? 'bg-emerald-500/80' :
                                                campaign.status === 'PENDING' ? 'bg-indigo-600/80' :
                                                campaign.status === 'FAILED' ? 'bg-rose-500/80' : 'bg-amber-500/80'
                                            }`}>
                                                {campaign.status === 'COMPLETED' ? 'Sent' :
                                                 campaign.status === 'PENDING' ? 'Queued' :
                                                 campaign.status === 'PROCESSING' ? 'Sending' : 'Failed'}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between text-[10px] text-zinc-400 font-bold uppercase tracking-wider">
                                            <div className="flex items-center gap-1">
                                                <Calendar size={11} />
                                                <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
                                            </div>
                                            <div>{campaign.contacts_count} Targets</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Report"
                message="Are you sure you want to permanently remove this report entry? This action cannot be undone."
                loading={isDeleting}
            />
        </div>
    );
};

export default Reports;
